import React from "react";
import { Link } from "react-router-dom";
import RichEditor from "../../../components/dashboard/RichEditor";
import { InternalGistLinkComposer } from "./InternalGistLinkComposer";
import styles from "../SubjectSinglePage.module.css";
import type { GistItem, GistReaderMode } from "../model/types";
import {
  canUserEditGist,
  countGistMetrics,
  getReplyGists,
} from "../model/subjectSingleModel";

type ToolsModeProps = {
  gist: GistItem;
  user: any;
  gists: GistItem[];
  selectedSubjectId: number | null;
  isRegisteredUser: boolean;
  toolsEditingGistId: number | null;
  summaryDraft: string;
  bodyDraft: string;
  readerEditError: string | null;
  isSavingReaderEdit: boolean;
  toolsCopyStatus: string | null;
  readModeContent: React.ReactNode;
  onCreateReply: (subjectId: number, parentGistId: number) => void;
  onChangeReaderMode: (mode: GistReaderMode) => void;
  onBeginToolsEdit: (gist: GistItem) => void;
  onCopyWorkspaceGistLink: (gist: GistItem) => void;
  getNavigatorGistUrl: (gist: GistItem) => string;
  onCancelToolsEdit: () => void;
  onSaveToolsEdit: (gist: GistItem) => void;
  onSummaryDraftChange: React.Dispatch<React.SetStateAction<string>>;
  onBodyDraftChange: React.Dispatch<React.SetStateAction<string>>;
  getInternalGistLinkUrl?: (gist: GistItem) => string;
};

export const ToolsMode: React.FC<ToolsModeProps> = ({
  gist,
  user,
  gists,
  selectedSubjectId,
  isRegisteredUser,
  toolsEditingGistId,
  summaryDraft,
  bodyDraft,
  readerEditError,
  isSavingReaderEdit,
  toolsCopyStatus,
  readModeContent,
  onCreateReply,
  onChangeReaderMode,
  onBeginToolsEdit,
  onCopyWorkspaceGistLink,
  getNavigatorGistUrl,
  onCancelToolsEdit,
  onSaveToolsEdit,
  onSummaryDraftChange,
  onBodyDraftChange,
  getInternalGistLinkUrl,
}) => {
  const metrics = countGistMetrics(gist);
  const replies = getReplyGists(gist, gists).length;
  const subjectId = selectedSubjectId || gist.subjectId;
  const canCreateReply = Boolean(subjectId && isRegisteredUser);
  const canEdit = canUserEditGist(gist, user);
  const statusLabel = gist.status || (gist.publishedAt ? "published" : "draft");

  return (
    <section
      className={styles.toolsReadingWorkspace}
      aria-label="Gist tools with reading context"
    >
      <div className={styles.toolsInlineBar}>
        <div>
          <span className={styles.modeEyebrow}>Tools</span>
          <h2>Tools for the current reading strip</h2>
          <p>
            The same full gist sequence from Read stays visible here. Use these
            quick actions without losing the surrounding parent/current/reply
            reading context.
          </p>
        </div>
        <div className={styles.toolsInlineMeta} aria-label="Current gist status">
          <span>{statusLabel}</span>
          <span>{replies} replies</span>
          <span>{metrics.commentCount} comments</span>
        </div>
      </div>

      <div className={styles.toolsInlineActions} aria-label="Quick gist actions">
        <button
          type="button"
          disabled={!canCreateReply}
          onClick={() => {
            if (!subjectId) return;
            onCreateReply(subjectId, gist.id);
          }}
        >
          ↪ {isRegisteredUser ? "Reply with new GIST" : "Login required"}
        </button>
        <button
          type="button"
          title="Open Advanced to use the full comment workspace for this gist."
          onClick={() => onChangeReaderMode("advanced")}
        >
          ▢ Advanced comments
        </button>
        <button
          type="button"
          title="Open Advanced to use anchored comments and annotations."
          onClick={() => onChangeReaderMode("advanced")}
        >
          ✎ Advanced annotations
        </button>
        <button
          type="button"
          title="Open Advanced to use the full editor and version controls."
          onClick={() => onChangeReaderMode("advanced")}
        >
          ⌘ {canEdit ? "Advanced editor" : "Advanced workspace"}
        </button>
        <button
          type="button"
          disabled={!canEdit}
          title={canEdit ? "Edit the current gist summary and body here." : "You cannot edit this gist."}
          onClick={() => onBeginToolsEdit(gist)}
        >
          Edit gist
        </button>
        <button type="button" onClick={() => onCopyWorkspaceGistLink(gist)}>
          ⇪ Share / Copy link
        </button>
        <Link to={getNavigatorGistUrl(gist)}>Open in Navigator</Link>
        {toolsCopyStatus && <strong>{toolsCopyStatus}</strong>}
      </div>

      {toolsEditingGistId === gist.id && (
        <section
          className={styles.toolsInlineEditor}
          aria-label={`Lightweight editor for ${gist.title}`}
        >
          <div className={styles.toolsInlineEditorHeader}>
            <div>
              <span className={styles.modeEyebrow}>Quick edit</span>
              <h3>Edit gist</h3>
              <p>
                Saves through the existing version/edit history. Title changes
                stay in the full Advanced workspace.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onChangeReaderMode("advanced")}
            >
              Open full Advanced editor
            </button>
          </div>

          <label className={styles.toolsInlineField}>
            <span>Title</span>
            <input type="text" value={gist.title} readOnly />
          </label>

          <label className={styles.toolsInlineField}>
            <span>Point / summary</span>
            <textarea
              value={summaryDraft}
              maxLength={300}
              rows={3}
              onChange={(event) =>
                onSummaryDraftChange(event.target.value.slice(0, 300))
              }
            />
          </label>

          <div className={styles.toolsInlineField}>
            <span>Body</span>
            <div className={styles.toolsInlineRichEditor}>
              {getInternalGistLinkUrl && (
                <InternalGistLinkComposer
                  currentGistId={gist.id}
                  gists={gists}
                  bodyDraft={bodyDraft}
                  disabled={isSavingReaderEdit}
                  getGistLinkUrl={getInternalGistLinkUrl}
                  onBodyDraftChange={onBodyDraftChange}
                />
              )}
              <RichEditor
                editable={true}
                content={bodyDraft}
                setContent={onBodyDraftChange}
              />
            </div>
          </div>

          {readerEditError && (
            <div className={styles.readerEditError}>{readerEditError}</div>
          )}

          <div className={styles.toolsInlineEditorActions}>
            <button
              type="button"
              onClick={onCancelToolsEdit}
              disabled={isSavingReaderEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSaveToolsEdit(gist)}
              disabled={isSavingReaderEdit}
            >
              {isSavingReaderEdit ? "Saving..." : "Save"}
            </button>
          </div>
        </section>
      )}

      {readModeContent}
    </section>
  );
};
